import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, TrendingUp, Send, Clock, CheckCircle2, X, BarChart3, XCircle } from "lucide-react";

const STATUS_COLORS = {
  draft:     { bg: "bg-gray-500/10", text: "text-gray-400", border: "border-gray-500/30", icon: Clock, label: "Borrador" },
  scheduled: { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/30", icon: Clock, label: "Programada" },
  sending:   { bg: "bg-primary/10", text: "text-primary", border: "border-primary/30", icon: Send, label: "Enviando" },
  sent:      { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/30", icon: CheckCircle2, label: "Enviada" },
  canceled:  { bg: "bg-destructive/10", text: "text-destructive", border: "border-destructive/30", icon: XCircle, label: "Cancelada" },
};

function fmtNumber(n) { return Number(n || 0).toLocaleString("es-MX"); }
function pct(x, y) { return y > 0 ? Math.round((x / y) * 1000) / 10 : 0; }

export default function EmailCampaigns() {
  const { user } = useAuth();
  const nav = useNavigate();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const base = superadmin ? "/app/superadmin" : "/app/staff";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [templates, setTemplates] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const q = statusFilter ? `?status=${statusFilter}` : "";
      const [r1, r2] = await Promise.all([
        api.get(`/admin/email-campaigns${q}`),
        api.get("/admin/email-templates"),
      ]);
      setItems(r1.data.items || []);
      setTemplates(r2.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const cancel = async (c) => {
    if (!confirm(`¿Cancelar campaña "${c.name}"?`)) return;
    try {
      await api.post(`/admin/email-campaigns/${c.id}/cancel`);
      toast.success("Campaña cancelada");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const totalSent = items.reduce((s, x) => s + (x.metrics?.sent || 0), 0);
  const totalOpened = items.reduce((s, x) => s + (x.metrics?.opened || 0), 0);
  const totalClicked = items.reduce((s, x) => s + (x.metrics?.clicked || 0), 0);

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div>
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-primary font-semibold">
              Fase 13.5 · Email marketing
            </p>
            <h1 className="mt-2 text-3xl font-bold text-white font-display">Campañas</h1>
            <p className="mt-1 text-sm text-gray-400">
              Envío MOCKEADO — no se envía email real. Métricas simuladas para demo.
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              to={`${base}/email/templates`}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-md border border-gray-800 text-white text-sm font-semibold hover:bg-white/5"
            >
              Plantillas
            </Link>
            <button
              onClick={() => setShowNew(true)}
              data-testid="campaign-new-btn"
              className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-primary hover:brightness-110 text-primary-foreground text-sm font-semibold shadow-sm"
            >
              <Plus className="w-4 h-4" /> Nueva campaña
            </button>
          </div>
        </div>

        {/* KPIs globales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <KpiCard label="Campañas activas" value={items.filter((x) => x.status === "sending" || x.status === "scheduled").length} icon={Send} />
          <KpiCard label="Enviados totales" value={fmtNumber(totalSent)} icon={BarChart3} />
          <KpiCard label="Open rate global" value={`${pct(totalOpened, totalSent)}%`} icon={TrendingUp} accent />
          <KpiCard label="Click rate global" value={`${pct(totalClicked, totalSent)}%`} icon={TrendingUp} accent />
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          {[
            { v: "", l: "Todas" },
            { v: "draft", l: "Borradores" },
            { v: "scheduled", l: "Programadas" },
            { v: "sending", l: "Enviando" },
            { v: "sent", l: "Enviadas" },
            { v: "canceled", l: "Canceladas" },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setStatusFilter(f.v)}
              className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest border ${
                statusFilter === f.v
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-gray-800 text-gray-400 hover:bg-white/5"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto border border-gray-800 rounded-xl" data-testid="campaigns-table">
          <table className="w-full text-sm">
            <thead className="bg-[#0B0F14] text-xs uppercase tracking-widest text-gray-400">
              <tr>
                <th className="text-left px-3 py-2">Campaña</th>
                <th className="text-left px-3 py-2">Estado</th>
                <th className="text-left px-3 py-2">Tipo</th>
                <th className="text-right px-3 py-2">Audiencia</th>
                <th className="text-right px-3 py-2">Enviados</th>
                <th className="text-right px-3 py-2">Open %</th>
                <th className="text-right px-3 py-2">Click %</th>
                <th className="text-right px-3 py-2">Bounce %</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="text-center text-gray-500 py-6">Cargando…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={9} className="text-center text-gray-500 py-6">Sin campañas.</td></tr>
              )}
              {items.map((c) => {
                const st = STATUS_COLORS[c.status] || STATUS_COLORS.draft;
                const StIcon = st.icon;
                const m = c.metrics || {};
                return (
                  <tr key={c.id} className="border-t border-gray-800 hover:bg-white/5" data-testid={`campaign-row-${c.slug}`}>
                    <td className="px-3 py-2">
                      <Link
                        to={`${base}/email/campaigns/${c.id}`}
                        className="font-semibold text-white hover:text-primary"
                        data-testid={`campaign-open-${c.slug}`}
                      >
                        {c.name}
                      </Link>
                      <p className="text-xs text-gray-500 font-mono">{c.template_name || c.template_slug}</p>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded uppercase font-bold border ${st.bg} ${st.text} ${st.border}`}>
                        <StIcon className="w-3 h-3" /> {st.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-400">
                      {c.send_type === "drip_trigger" ? (
                        <span>Drip · {c.trigger_event}</span>
                      ) : (
                        <span>One-shot</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-white tabular-nums">
                      {fmtNumber(c.audience_count)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-white tabular-nums">
                      {fmtNumber(m.sent)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-primary font-bold tabular-nums">
                      {pct(m.opened, m.sent)}%
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-primary font-bold tabular-nums">
                      {pct(m.clicked, m.sent)}%
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-gray-400 tabular-nums">
                      {pct(m.bounced, m.sent)}%
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Link
                          to={`${base}/email/campaigns/${c.id}/metrics`}
                          className="p-1.5 rounded text-gray-400 hover:text-primary"
                          aria-label="Ver métricas"
                          data-testid={`campaign-metrics-${c.slug}`}
                        >
                          <BarChart3 className="w-4 h-4" />
                        </Link>
                        {(c.status === "scheduled" || c.status === "sending") && (
                          <button
                            onClick={() => cancel(c)}
                            className="p-1.5 rounded text-gray-400 hover:text-destructive"
                            aria-label="Cancelar"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showNew && (
        <NewCampaignModal
          templates={templates}
          onClose={() => setShowNew(false)}
          onCreated={(c) => nav(`${base}/email/campaigns/${c.id}`)}
        />
      )}
    </StaffLayout>
  );
}

function KpiCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="p-4 rounded-xl border border-gray-800 bg-[#111827]">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-500 font-semibold">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <p className={`mt-2 text-2xl font-extrabold tabular-nums ${accent ? "text-primary" : "text-white"}`}>
        {value}
      </p>
    </div>
  );
}

function NewCampaignModal({ templates, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    template_id: templates[0]?.id || "",
    send_type: "one_shot",
    trigger_event: "order_placed",
    trigger_delay_minutes: 0,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        template_id: form.template_id,
        send_type: form.send_type,
        segment: { roles: ["alumno"], exclude_unsubscribed: true },
      };
      if (form.send_type === "drip_trigger") {
        payload.trigger_event = form.trigger_event;
        payload.trigger_delay_minutes = Number(form.trigger_delay_minutes) || 0;
      }
      const r = await api.post("/admin/email-campaigns", payload);
      toast.success("Campaña creada");
      onCreated(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        data-testid="new-campaign-modal"
        className="w-full max-w-lg bg-[#111827] border border-gray-800 rounded-xl p-5 space-y-3"
      >
        <div className="flex justify-between">
          <h3 className="text-xl font-bold text-white font-display">Nueva campaña</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white">
            <X />
          </button>
        </div>
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Nombre *</span>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            data-testid="new-campaign-name"
            className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-primary"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Plantilla base *</span>
          <select
            value={form.template_id}
            onChange={(e) => setForm({ ...form, template_id: e.target.value })}
            required
            data-testid="new-campaign-template"
            className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white"
          >
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.name} · {t.category}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Tipo de envío</span>
          <select
            value={form.send_type}
            onChange={(e) => setForm({ ...form, send_type: e.target.value })}
            data-testid="new-campaign-sendtype"
            className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white"
          >
            <option value="one_shot">One-shot (envío único)</option>
            <option value="drip_trigger">Drip (por evento)</option>
          </select>
        </label>
        {form.send_type === "drip_trigger" && (
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Evento trigger</span>
              <select
                value={form.trigger_event}
                onChange={(e) => setForm({ ...form, trigger_event: e.target.value })}
                className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white"
              >
                <option value="order_placed">order_placed</option>
                <option value="program_completed">program_completed</option>
                <option value="subscription_activated">subscription_activated</option>
                <option value="cart_abandoned">cart_abandoned</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Delay (min)</span>
              <input
                type="number"
                value={form.trigger_delay_minutes}
                onChange={(e) => setForm({ ...form, trigger_delay_minutes: e.target.value })}
                className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white"
              />
            </label>
          </div>
        )}
        <div className="flex gap-2 pt-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2 rounded-md border border-gray-800 text-white text-sm font-semibold hover:bg-white/5"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy || !form.name || !form.template_id}
            data-testid="new-campaign-submit"
            className="flex-1 py-2 rounded-md bg-primary hover:brightness-110 text-primary-foreground text-sm font-semibold disabled:opacity-60"
          >
            {busy ? "…" : "Crear y editar"}
          </button>
        </div>
      </form>
    </div>
  );
}
